// src/views/dashboard/salesrep/ProvidersModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoCloseOutline,
  IoCheckmarkCircle,
  IoMail,
  IoCall,
  IoLocation,
  IoSearchOutline,
  IoAlertCircle,
} from "react-icons/io5";
import { Loader2, Users, Award, TrendingUp } from "lucide-react";

/**
 * ProMed Health Plus — Providers Modal (Professional / Teal Theme)
 * - Cleaner, less “flashy”, more enterprise/clinical
 * - Strong dark-mode surfaces
 * - Search + stats + polished provider cards
 */

const ProvidersModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  const [providers, setProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (isOpen) fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const fetchProviders = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const response = await fetch(
        "https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/sales-rep/providers/",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch providers");

      const data = await response.json();
      setProviders(data.data || []);
    } catch (err) {
      console.error("Error fetching providers:", err);
      setErrorMessage(err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProviders = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return providers;
    return providers.filter((p) => {
      const name = String(p.name || "").toLowerCase();
      const email = String(p.email || "").toLowerCase();
      const phone = String(p.phone || "").toLowerCase();
      const facility = String(p.facility || "").toLowerCase();
      return (
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        facility.includes(q)
      );
    });
  }, [providers, searchText]);

  const totals = useMemo(() => {
    const sum = (arr, key) =>
      arr.reduce((acc, x) => acc + (Number(x?.[key]) || 0), 0);
    return {
      providers: providers.length,
      patients: sum(providers, "patient_count"),
      ivrs: sum(providers, "ivr_count"),
      orders: sum(providers, "order_count"),
    };
  }, [providers]);

  const initials = (name) => {
    const parts = String(name || "P").trim().split(" ").filter(Boolean);
    const a = parts[0]?.[0] || "P";
    const b = parts[1]?.[0] || "";
    return `${a}${b}`.toUpperCase();
  };

  const formatJoined = (dateJoined) => {
    if (!dateJoined) return "N/A";
    const d = new Date(dateJoined);
    if (Number.isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const goToPatients = (providerId) => {
    onClose?.();
    navigate(`/sales-rep/providers/${providerId}/patients`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative mx-auto flex min-h-full max-w-6xl items-center justify-center p-4">
        <div className="w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
          {/* Header */}
          <div className="border-b border-slate-200 bg-white/80 px-6 py-5 backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-teal-50 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:ring-teal-500/20">
                  <Users className="text-teal-700 dark:text-teal-300" size={22} />
                </div>

                <div>
                  <h2 className="text-xl font-extrabold text-slate-900 dark:text-slate-100 sm:text-2xl">
                    Providers
                  </h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    Browse your assigned provider network.
                  </p>

                  {/* Summary chips */}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Chip label="Providers" value={totals.providers} />
                    <Chip label="Patients" value={totals.patients} />
                    <Chip label="IVRs" value={totals.ivrs} />
                    <Chip label="Orders" value={totals.orders} />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="hidden w-[340px] items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:flex">
                  <IoSearchOutline className="text-lg text-slate-400" />
                  <input
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search by name, email, phone, facility…"
                    className="w-full bg-transparent text-sm font-semibold text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-slate-100"
                  />
                </div>

                <button
                  onClick={onClose}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
                  aria-label="Close"
                >
                  <IoCloseOutline className="text-2xl" />
                </button>
              </div>
            </div>

            {/* Mobile search */}
            <div className="mt-4 sm:hidden">
              <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                <IoSearchOutline className="text-lg text-slate-400" />
                <input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search providers…"
                  className="w-full bg-transparent text-sm font-semibold text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-slate-100"
                />
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="max-h-[80vh] overflow-y-auto px-6 py-6">
            {isLoading ? (
              <LoadingState />
            ) : errorMessage ? (
              <ErrorState
                message={errorMessage}
                onRetry={fetchProviders}
                onClose={onClose}
              />
            ) : providers.length === 0 ? (
              <EmptyState />
            ) : filteredProviders.length === 0 ? (
              <NoResultsState onClear={() => setSearchText("")} />
            ) : (
              <div className="grid gap-4">
                {filteredProviders.map((provider) => (
                  <div
                    key={provider.id}
                    className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-900/50"
                  >
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      {/* Left */}
                      <div className="flex min-w-0 items-start gap-4">
                        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-teal-600 to-emerald-600 text-white shadow-sm">
                          <span className="text-sm font-extrabold tracking-wide">
                            {initials(provider.name)}
                          </span>
                        </div>

                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="truncate text-base font-extrabold text-slate-900 dark:text-slate-100 sm:text-lg">
                              {provider.name}
                            </h3>

                            <div className="flex flex-wrap gap-2">
                              {provider.is_verified ? (
                                <Badge
                                  tone="good"
                                  icon={<IoCheckmarkCircle />}
                                  text="Verified"
                                />
                              ) : null}
                              {provider.is_approved ? (
                                <Badge
                                  tone="info"
                                  icon={<Award size={14} />}
                                  text="Approved"
                                />
                              ) : null}
                              {provider.has_signed_baa ? (
                                <Badge
                                  tone="brand"
                                  icon={<IoCheckmarkCircle />}
                                  text="BAA Signed"
                                />
                              ) : null}
                            </div>
                          </div>

                          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <ContactRow
                              icon={
                                <IoMail className="text-teal-700 dark:text-teal-300" />
                              }
                              label="Email"
                              value={provider.email || "N/A"}
                            />
                            <ContactRow
                              icon={
                                <IoCall className="text-teal-700 dark:text-teal-300" />
                              }
                              label="Phone"
                              value={provider.phone || "N/A"}
                            />
                            <ContactRow
                              icon={
                                <IoLocation className="text-teal-700 dark:text-teal-300" />
                              }
                              label="Facility"
                              value={provider.facility || "N/A"}
                            />
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-950/40">
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                Joined
                              </p>
                              <p className="truncate text-sm font-extrabold text-slate-900 dark:text-slate-100">
                                {formatJoined(provider.date_joined)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right: Stats + CTA */}
                      <div className="flex flex-col gap-3 lg:w-[360px]">
                        <div className="grid grid-cols-3 gap-3">
                          <StatTile label="Patients" value={provider.patient_count} />
                          <StatTile label="IVRs" value={provider.ivr_count} />
                          <StatTile label="Orders" value={provider.order_count} />
                        </div>

                        <button
                          onClick={() => goToPatients(provider.id)}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-3 text-sm font-extrabold text-white shadow-sm transition hover:from-teal-700 hover:to-emerald-700 hover:shadow-md"
                        >
                          View Patients
                          <TrendingUp size={16} />
                        </button>

                        <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                          <span className="inline-flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-emerald-500" />
                            Active in network
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-200 bg-white px-6 py-4 dark:border-slate-800 dark:bg-slate-950">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Tip: Use the search bar to quickly find a provider by name or email.
              </p>
              <button
                onClick={onClose}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/** ---------- UI Bits ---------- */

const Chip = ({ label, value }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
    <span className="text-slate-500 dark:text-slate-400">{label}</span>
    <span className="font-extrabold text-slate-900 dark:text-slate-100">{value}</span>
  </span>
);

const Badge = ({ tone, icon, text }) => {
  const toneClass =
    tone === "good"
      ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30"
      : tone === "info"
      ? "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/30"
      : "bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-500/30";

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold ring-1 ${toneClass}`}>
      <span className="text-sm">{icon}</span>
      {text}
    </span>
  );
};

const ContactRow = ({ icon, label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-950/40">
    <div className="flex items-start gap-3">
      <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl bg-teal-50 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:ring-teal-500/20">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {label}
        </p>
        <p className="truncate text-sm font-extrabold text-slate-900 dark:text-slate-100">
          {value}
        </p>
      </div>
    </div>
  </div>
);

const StatTile = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-white/70 px-3 py-3 text-center dark:border-slate-800 dark:bg-slate-950/40">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {label}
    </p>
    <p className="mt-1 text-lg font-extrabold text-slate-900 dark:text-slate-100">
      {Number(value) || 0}
    </p>
  </div>
);

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center py-16">
    <Loader2 className="h-12 w-12 animate-spin text-teal-600" />
    <p className="mt-4 text-sm font-semibold text-slate-600 dark:text-slate-400">
      Loading providers…
    </p>
  </div>
);

const ErrorState = ({ message, onRetry, onClose }) => (
  <div className="rounded-2xl border border-rose-200 bg-white p-6 shadow-sm dark:border-rose-500/30 dark:bg-slate-900/70">
    <div className="flex items-start gap-3">
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-rose-50 ring-1 ring-rose-200 dark:bg-rose-500/10 dark:ring-rose-500/30">
        <IoAlertCircle className="text-xl text-rose-600 dark:text-rose-300" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
          Unable to load providers
        </p>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {message}
        </p>
      </div>
    </div>

    <div className="mt-5 flex gap-3">
      <button
        onClick={onClose}
        className="inline-flex flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
      >
        Close
      </button>
      <button
        onClick={onRetry}
        className="inline-flex flex-1 items-center justify-center rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-2.5 text-sm font-extrabold text-white shadow-sm transition hover:from-teal-700 hover:to-emerald-700 hover:shadow-md"
      >
        Retry
      </button>
    </div>
  </div>
);

const EmptyState = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900/50">
    <Users className="mx-auto mb-4 h-14 w-14 text-slate-400" />
    <p className="text-lg font-extrabold text-slate-900 dark:text-slate-100">
      No providers yet
    </p>
    <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
      Providers will appear here once assigned.
    </p>
  </div>
);

const NoResultsState = ({ onClear }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900/50">
    <IoAlertCircle className="mx-auto mb-3 text-4xl text-slate-400" />
    <p className="text-lg font-extrabold text-slate-900 dark:text-slate-100">
      No matches
    </p>
    <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
      Try searching by name, email, phone, or facility.
    </p>
    <button
      onClick={onClear}
      className="mt-5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
    >
      Clear search
    </button>
  </div>
);

export default ProvidersModal;
